import React, { FC, useState, useMemo, useContext } from 'react';
import {
    Checkbox,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useEditLab } from 'context/edit-lab-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { GroupMaterials, ExtendedMaterialGroup } from 'models/organization';

type PriceDifferenceDialogProps = {
    open: boolean;
    onClose: () => void;
    material: GroupMaterials;
    group: ExtendedMaterialGroup;
};

const PriceDifferenceDialog: FC<PriceDifferenceDialogProps> = ({ open, onClose, group }) => {
    const { t: translate } = useTranslation(['common', 'organization']);
    const { updateMaterialPriceDiff } = useEditLab();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [priceValues, setPriceValues] = useState<{ [key: string]: string }>({});
    const [availabilityValues, setAvailabilityValues] = useState<{ [key: string]: boolean }>({});
    const [isSaving, setIsSaving] = useState(false);

    const initialValues = useMemo(() => {
        if (!group?.materials) return {};

        const initial: Record<string, string> = {};
        const diffGroup = group.materialPriceDifferenceForGroup?.[0]?.materials || [];

        group.materials.forEach((m) => {
            if (!m._id) return;
            const found = diffGroup.find((item) => item.materialName === m.material && item.priceDiff !== null);
            initial[m._id] = found ? found.priceDiff.toString() : '';
        });

        return initial;
    }, [group]);

    const initialAvailabilityValues = useMemo(() => {
        if (!group?.materials) return {};

        const initial: Record<string, boolean> = {};
        const diffGroup = group.materialPriceDifferenceForGroup?.[0]?.materials || [];

        group.materials.forEach((m) => {
            if (!m._id) return;
            const found = diffGroup.find((item) => item.materialName === m.material);
            initial[m._id] = found?.isAvailable !== false;
        });

        return initial;
    }, [group]);

    const currentPriceValues = useMemo(() => {
        return { ...initialValues, ...priceValues };
    }, [initialValues, priceValues]);

    const currentAvailabilityValues = useMemo(() => {
        return { ...initialAvailabilityValues, ...availabilityValues };
    }, [initialAvailabilityValues, availabilityValues]);

    const hasChanges = useMemo(() => {
        const priceChanged = Object.keys(initialValues).some((key) => initialValues[key] !== currentPriceValues[key]);
        const availabilityChanged = Object.keys({ ...initialAvailabilityValues, ...availabilityValues }).length > 0;
        return priceChanged || availabilityChanged;
    }, [initialValues, currentPriceValues, initialAvailabilityValues, currentAvailabilityValues]);

    const handlePriceChange = (id: string, value: string) => {
        setPriceValues((prev) => ({ ...prev, [id]: value }));
    };

    const handleAvailabilityChange = (id: string, checked: boolean) => {
        setAvailabilityValues((prev) => ({ ...prev, [id]: checked }));
    };

    const handleSave = async () => {
        if (isSaving) return;

        setIsSaving(true);

        const materials = (group?.materials || []).reduce(
            (acc: { material: string; priceDiff: string | null; isAvailable: boolean }[], m: GroupMaterials) => {
                if (!m._id) return acc;
                const priceDiff = currentPriceValues[m._id]?.trim() || null;
                const isAvailable = currentAvailabilityValues[m._id] ?? true;
                acc.push({ material: m._id, priceDiff, isAvailable });
                return acc;
            },
            [],
        );

        try {
            await updateMaterialPriceDiff(group._id, materials);

            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('organization:PRICE_DIFFERENCES_SAVED_SUCCESSFULLY'),
            });

            onClose();
        } catch (error) {
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('organization:FAILED_TO_SAVE_PRICE_DIFFERENCES'),
            });
            console.error(`${translate('organization:ERROR_SAVING_PRICE_DIFFERENCES')}:`, error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <DHDialog
            title={translate('organization:PRICE_DIFFERENCE')}
            maxWidth="md"
            fullWidth
            open={open}
            onClose={onClose}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {translate('organization:MATERIAL_GROUP_NAME')}: {group?.name}
                </Typography>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('organization:MATERIAL_NAME')}</TableCell>
                            <TableCell>{translate('organization:PRICE_DIFFERENCE')}</TableCell>
                            <TableCell>{translate('organization:MATERIAL_AVAILABLE')}</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {group?.materials
                            ?.filter((m): m is GroupMaterials & { _id: string } => Boolean(m._id))
                            .map((m) => (
                                <TableRow key={m._id}>
                                    <TableCell>{m.material}</TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={currentPriceValues[m._id] || ''}
                                            onChange={(e) => handlePriceChange(m._id, e.target.value)}
                                            placeholder={translate('organization:ENTER_PRICE_DIFFERENCE')}
                                            sx={{ width: '150px' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox
                                            checked={currentAvailabilityValues[m._id] ?? true}
                                            onChange={(e) => handleAvailabilityChange(m._id, e.target.checked)}
                                            color="secondary"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <LoadingButton
                    variant="outlined"
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                    loading={isSaving}
                >
                    {translate('common:SAVE')}
                </LoadingButton>
            </Box>
        </DHDialog>
    );
};

export default PriceDifferenceDialog;
